import type { Language, Translations } from '@/types';

export const translations: Translations = {
  fr: {
    // Hero
    'hero-title': 'Applications web & mobiles sur mesure pour PME et startups',
    'hero-stack': 'Flutter • React / Next.js • APIs robustes',
    'hero-description':
      "Nous concevons et développons des applications performantes, évolutives et maintenables, de l'idée à la mise en production.",
    'hero-cta': 'Discuter de votre projet',

    // Navigation
    about: 'À Propos',
    services: 'Services',
    team: 'Notre Équipe',
    contact: 'Contact',

    // About section
    'about-title': 'Notre ADN',
    'about-description':
      'Du code propre, une communication claire et des produits qui durent. Nous sommes DevMeUp.',
    'audience-title': 'Pour qui ?',
    'audience-list':
      '<ul class="mt-3 space-y-1 text-left text-gray-400"><li><span class="font-semibold text-white">PME</span> : Digitalisation & Outils</li><li><span class="font-semibold text-white">Startups</span> : MVP & Scale</li><li><span class="font-semibold text-white">Agences</span> : Renfort Expert</li></ul>',
    'why-title': 'Pourquoi nous ?',
    'why-list':
      '<ul class="mt-3 space-y-1 text-left text-gray-400"><li>✓ Studio structuré (pas de freelance isolé)</li><li>✓ Stack moderne & pérenne</li><li>✓ Engagement délais & qualité</li></ul>',
    'process-title': 'Méthodologie',
    'process-list':
      '<ol class="mt-3 space-y-1 text-left text-gray-400 list-decimal list-inside"><li>Compréhension</li><li>Chiffrage clair</li><li>Développement agile</li><li>Livraison & Tests</li><li>Suivi</li></ol>',

    // Services section
    'services-title': 'Nos Services',
    'mobile-dev-title': 'Apps mobiles',
    'mobile-dev-description':
      'Développement iOS et Android natif ou cross-plateforme (Flutter) pour un lancement rapide et maîtrisé.',
    'web-dev-title': 'Web & SaaS',
    'web-dev-description':
      'Plateformes web modernes, dashboards et outils métiers performants avec React et Next.js.',
    'backend-title': 'Back-end & API',
    'backend-description':
      'Architectures robustes et sécurisées (NestJS/Node.js) pour propulser vos services.',
    'maintenance-title': 'Maintenance',
    'maintenance-description':
      'Suivi technique, mises à jour et évolutions pour garantir la pérennité de votre projet.',

    // Packaged offers (Services)
    'offers-title': 'Nos Formules',
    'offers-intro':
      "Des solutions claires adaptées à chaque stade de développement. Du démarrage rapide à la mise à l'échelle.",
    'offers-positioning':
      'Studio web & mobile expert <span class="text-white font-semibold">Flutter</span> & <span class="text-white font-semibold">React</span>. Basé à Perpignan, opérant partout.',

    'offer-section-who': 'Cible',
    'offer-section-includes': 'Inclus',
    'offer-section-result': 'Résultat',
    'offer-section-details': 'Infos',
    'offer-section-examples': 'Exemples',
    'offer-section-what': 'Services',
    'offer-section-formats': 'Formats',

    'offer1-badge': 'Populaire',
    'offer1-title': 'Pack MVP Express',
    'offer1-subtitle': 'Validez votre idée avec un produit fonctionnel lancé en 6 à 8 semaines.',
    'offer1-who': 'Startups & Entrepreneurs voulant confronter leur vision au marché.',
    'offer1-includes':
      '<ul class="space-y-1 text-sm text-gray-300"><li>✓ Atelier de cadrage</li><li>✓ App Mobile (iOS/Android)</li><li>✓ App Web React</li><li>✓ Backend & API Sécurisés</li><li>✓ Mise en production</li></ul>',
    'offer1-result': 'Un produit prêt à accueillir vos premiers utilisateurs.',
    'offer1-details':
      '<div class="text-sm text-gray-300"><div>⏱ 6-8 semaines</div><div>💰 Dès 15k€ HT</div></div>',

    'offer2-badge': 'Sur Mesure',
    'offer2-title': 'Projets Spécifiques',
    'offer2-subtitle':
      'Outils métiers, portails clients ou architectures complexes : votre besoin, notre expertise.',
    'offer2-who': 'PME cherchant à digitaliser leurs processus clés.',
    'offer2-examples':
      '<ul class="space-y-1 text-sm text-gray-300"><li>• Portails clients</li><li>• Outils internes</li><li>• Dashboards BI</li></ul>',
    'offer2-includes':
      '<ul class="space-y-1 text-sm text-gray-300"><li>✓ Analyse sur mesure</li><li>✓ Dév. Web & Mobile</li><li>✓ Intégrations API</li><li>✓ Formation & Doc</li></ul>',
    'offer2-details':
      '<div class="text-sm text-gray-300"><div>⏱ Selon périmètre</div><div>💰 Sur devis</div></div>',

    'offer3-badge': 'Renfort',
    'offer3-title': 'Expertise Technique',
    'offer3-subtitle': 'Intégrez des développeurs seniors dans votre équipe existante.',
    'offer3-who': "Agences & ESN ayant besoin de capacité ou d'expertise supplémentaire.",
    'offer3-what':
      '<ul class="space-y-1 text-sm text-gray-300"><li>✓ Renfort React / Flutter</li><li>✓ Code Review & Audit</li><li>✓ Marque blanche possible</li></ul>',
    'offer3-formats':
      '<ul class="space-y-1 text-sm text-gray-300"><li>• Régie</li><li>• Forfait</li></ul>',
    'offer3-details':
      '<div class="text-sm text-gray-300"><div>💰 TJM adapté</div><div>📍 Remote / Sur site</div></div>',

    'cred-title': 'Preuves & crédibilité',
    'cred-list':
      '<ul class="mt-3 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2 text-sm text-gray-200"><li>✔️ 2 développeurs seniors</li><li>✔️ 10 ans d’expérience chacun</li><li>✔️ Projets web & mobile livrés en production</li><li>✔️ Code maintenable, documenté, orienté qualité</li><li>✔️ Société française (SAS)</li><li>✔️ Approche pragmatique, orientée résultats</li></ul>',

    'offers-cta-primary': 'Discuter de votre projet',
    'offers-cta-secondary': 'Demander un devis',

    // Team section
    'team-title': 'Notre Équipe',
    'laurent-role': 'Président de DevMeUp',
    'laurent-desc1':
      "Développeur Full-Stack orienté backend spécialisé en PHP, j'ai une solide expérience dans la création d'applications web complexes et scalables.",
    'laurent-desc2':
      "Spécialisé dans les API RESTful, j'assure le bon fonctionnement des architectures serveur tout en collaborant étroitement avec les équipes front-end pour des intégrations optimales",
    'jeremy-role': 'Co-fondateur de DevMeUp',
    'jeremy-desc1':
      "Développeur Full-Stack orienté Front-End spécialisé en React et Flutter, j'ai une solide expérience dans la création d'interfaces utilisateur performantes et réactives.",
    'jeremy-desc2':
      "Passionné par l'innovation, je m'efforce d'intégrer les meilleures pratiques pour garantir un code propre et scalable.",
    'kate-role': 'Responsable des Communications Numériques',
    'kate-desc1':
      "Gestion des communications digitales et de la communication client, incluant la stratégie de contenu, les réseaux sociaux, les relations presse et la création de contenus assistée par l'IA.",
    'kate-desc2':
      "Soutien à l'acquisition de clients et au développement de nouveaux projets grâce à une présence en ligne stratégique.",

    // Contact section
    'contact-title': 'Contactez-Nous',
    'contact-hook': 'Vous avez un projet ou un besoin technique ?',
    'contact-subhook': '📩 Contactez-nous pour un échange sans engagement.',
    'name-label': 'Nom',
    'name-placeholder': 'Nom',
    'email-label': 'Email',
    'email-placeholder': 'Email',
    'company-label': 'Société',
    'company-placeholder': 'Société',
    'phone-label': 'Téléphone',
    'phone-placeholder': 'Téléphone',
    'message-label': 'Votre message',
    'message-placeholder': 'Votre message',
    'privacy-notice':
      "En soumettant ce formulaire, vous acceptez que le site DevMeUp mémorise et utilise vos données personnelles afin de pouvoir vous contacter. Elles sont conservées 30 jours au maximum par notre service de réception des messages. L'éditeur du site s'engage à ne pas transmettre vos coordonnées à quelconque tiers ni les utiliser pour de la publicité. Le formulaire est protégé contre les robots par Cloudflare Turnstile, sans cookie.",
    'accept-label': " J'autorise DevMeUp à communiquer avec moi ",
    'send-button': 'Demander un devis / Prendre rendez-vous',
    'quote-text': 'UN DEVIS,',
    'advice-text': 'DES CONSEILS,',
    'project-text': 'UN PROJET',
    'contact-us-text': "N'hésitez pas, contactez-nous !",
    'success-message': 'Message envoyé avec succès!',
    'error-message': "Erreur lors de l'envoi",

    // Additional contact form fields
    contact_subtitle:
      'Prêt à concrétiser votre projet ? Contactez-nous pour discuter de vos besoins.',
    address: 'Adresse',
    name: 'Nom complet',
    name_placeholder: 'Votre nom complet',
    email_placeholder: 'votre@email.com',
    phone_placeholder: 'Votre numéro de téléphone',
    service_type: 'Type de service',
    select_service: 'Sélectionnez un service',
    web_development: 'Développement Web',
    mobile_development: 'Développement Mobile',
    ui_design: "Design d'Interface",
    other: 'Autre',
    message: 'Message',
    message_placeholder: 'Décrivez votre projet...',
    send_message: 'Envoyer le message',

    // Form validation and status
    'form.submit': 'Envoyer',
    'form.sending': 'Envoi en cours...',
    'form.success.title': 'Message envoyé !',
    'form.success.message': 'Nous vous répondrons dans les plus brefs délais.',
    'form.error.title': 'Erreur',
    'form.error.submit':
      "Une erreur s'est produite lors de l'envoi. Écrivez-nous directement à contact@devmeup.fr.",
    'form.error.network': 'Erreur de connexion. Veuillez réessayer.',
    'form.error.captcha':
      "La vérification anti-robot n'a pas abouti. Patientez un instant, puis renvoyez le formulaire.",
    'form.error.fields': 'Merci de vérifier votre nom, votre adresse e-mail et votre message.',
    'form.error.rateLimited':
      'Vous avez envoyé plusieurs messages en peu de temps. Réessayez dans quelques minutes.',
    'form.error.required': 'Ce champ est requis',
    'form.error.email': 'Veuillez entrer une adresse email valide',
    'form.error.phone': 'Veuillez entrer un numéro de téléphone valide',
    'form.error.minLength': 'Le message doit contenir au moins 10 caractères',

    // Footer
    'footer-text':
      '&copy; 2026 DevMeUp. Tous droits réservés. <a href="mailto:contact@devmeup.fr" class="hover:underline">contact@devmeup.fr</a><br/>📍 Perpignan (66) – développement web & mobile<br/>⏱️ Réponse sous 48h<br/>🔒 Respect RGPD',
    'scroll-top-aria': 'Remonter en haut',

    // Portfolio section
    'portfolio-title': 'Portfolio',
    'portfolio-subtitle': 'Découvrez nos réalisations et applications mobiles.',
    'portfolio-dracailles-title': 'Fab Dracailles',
    'portfolio-dracailles-desc': 'Application de comptage de points pour les jeux de cartes',
    'portfolio-dracailles-link': 'Voir sur le Play Store',
    'portfolio-alarm-title': 'WeekUp',
    'portfolio-alarm-desc':
      '🚨 Nouvelle appli signée DevMeUp !\nUne alarme intelligente qui s’adapte à ton planning\n🗓 Une semaine sur deux ou toutes les semaines, c’est toi qui choisis !\nPlus besoin de tout reprogrammer, elle le fait pour toi 😎\nTestez-la et dites-nous ce que vous en pensez 🔥',
    'portfolio-alarm-link': 'Voir sur le Play Store',
    'portfolio-coming-title': 'À venir',
    'portfolio-coming-desc': 'De nouveaux projets seront bientôt ajoutés à notre portfolio.',
  },
  en: {
    // Hero
    'hero-title': 'Custom web & mobile apps for SMEs and startups',
    'hero-stack': 'Flutter • React / Next.js • Robust APIs',
    'hero-description':
      'We design and build high-performance, scalable and maintainable apps, from idea to production.',
    'hero-cta': 'Talk about your project',

    // Navigation
    about: 'About',
    services: 'Services',
    team: 'Our Team',
    contact: 'Contact',

    // About section
    'about-title': 'About',
    'about-description':
      'DevMeUp builds custom web & mobile applications, with a focus on clean code, delivery, and long-term maintainability.',
    'audience-title': 'Who is it for?',
    'audience-list':
      '<ul class="mt-3 space-y-2 text-left text-gray-400"><li><span class="font-semibold">SMEs</span>: process digitalization, internal tools, customer portals</li><li><span class="font-semibold">Startups</span>: fast and scalable MVPs</li><li><span class="font-semibold">Agencies & IT consultancies</span>: reliable subcontracting or team augmentation</li></ul>',
    'why-title': 'Why DevMeUp?',
    'why-list':
      '<ul class="mt-3 space-y-2 text-left text-gray-400"><li>✔️ A development company (not isolated freelancers)</li><li>✔️ Modern web & mobile expertise</li><li>✔️ Clean, documented, maintainable code</li><li>✔️ Clear communication and delivery commitment</li><li>✔️ Part-time / progressive involvement possible</li></ul>',
    'process-title': 'Our approach',
    'process-list':
      '<ol class="mt-3 space-y-2 text-left text-gray-400 list-decimal list-inside"><li>Discovery call and requirements understanding</li><li>Clear technical proposal and estimate</li><li>Iterative and transparent development</li><li>Delivery, testing, and production release</li><li>Follow-up and evolutions</li></ol>',

    // Services section
    'services-title': 'Our Services',
    'mobile-dev-title': 'Cross-platform mobile apps',
    'mobile-dev-description':
      'Build iOS and Android apps from a single codebase with Flutter to reduce costs and launch faster.',
    'web-dev-title': 'Web apps & SaaS',
    'web-dev-description':
      'Modern web platforms (dashboards, client portals, internal tools) with React and Next.js.',
    'backend-title': 'Back-end & APIs',
    'backend-description':
      'Secure and scalable APIs with NestJS / Node.js, integrated with your existing tools.',
    'maintenance-title': 'Maintenance & evolutions',
    'maintenance-description':
      'Continuous improvements, bug fixes, new features, and long-term technical support.',

    // Packaged offers (Services)
    'offers-title': 'Packaged offers',
    'offers-intro':
      '3 clear packages for SMEs, startups and team augmentation — built to move fast, ship clean, and scale safely.',
    'offers-positioning':
      '<span class="font-semibold text-white">Web & mobile development studio</span><br/>Specialized in <span class="font-semibold text-white">Flutter</span>, <span class="font-semibold text-white">React / Next.js</span> & <span class="font-semibold text-white">robust APIs</span>. Based in <span class="font-semibold text-white">Perpignan</span> (Occitanie) — on-site delivery possible in the <span class="font-semibold text-white">Pyrénées-Orientales</span> and remote.',

    'offer-section-who': 'Best for',
    'offer-section-includes': 'Includes',
    'offer-section-result': 'Outcome',
    'offer-section-details': 'Details',
    'offer-section-examples': 'Project examples',
    'offer-section-what': 'What we provide',
    'offer-section-formats': 'Engagement models',

    'offer1-badge': 'Best seller',
    'offer1-title': 'Web & Mobile MVP',
    'offer1-subtitle':
      'A complete MVP in 6–8 weeks to validate your idea and launch a solid product.',
    'offer1-who':
      'Startups and SMEs who want to launch quickly with a product that real users can actually use.',
    'offer1-includes':
      '<ul class="space-y-2 text-sm text-gray-200"><li>✔️ Discovery & scoping workshop</li><li>✔️ Flutter mobile app (iOS + Android)</li><li>✔️ React / Next.js web app</li><li>✔️ NestJS / Node.js backend & API</li><li>✔️ Authentication and roles</li><li>✔️ Database & scalable architecture</li><li>✔️ Tests, production release & documentation</li><li>✔️ 2 senior developers dedicated</li></ul>',
    'offer1-result':
      'A functional, clean, evolvable product ready for a first production release (with a realistic roadmap).',
    'offer1-details':
      '<ul class="space-y-2 text-sm text-gray-200"><li>⏱️ Timeline: <span class="font-semibold text-white">6–8 weeks</span></li><li>💰 Indicative budget: <span class="font-semibold text-white">from €15,000 (excl. VAT)</span> (depending on scope)</li><li>📍 Perpignan / 66: <span class="font-semibold text-white">on-site possible</span> + remote</li></ul>',

    'offer2-badge': 'Custom build',
    'offer2-title': 'Custom application (Web or Mobile)',
    'offer2-subtitle':
      'Internal tools, client portals or full products — we build what you actually need.',
    'offer2-who':
      'SMEs looking to digitalize a process, build an internal tool, or deliver an online service to customers.',
    'offer2-examples':
      '<ul class="space-y-2 text-sm text-gray-200"><li>• Client portal / extranet</li><li>• Internal business tool</li><li>• KPI dashboard</li><li>• Field mobile app</li><li>• Third-party API integrations</li></ul>',
    'offer2-includes':
      '<ul class="space-y-2 text-sm text-gray-200"><li>✔️ Needs analysis & scoping</li><li>✔️ Web or mobile development</li><li>✔️ Secure backend (if needed)</li><li>✔️ Integrations (APIs, payments, CRM, etc.)</li><li>✔️ Turnkey delivery</li><li>✔️ Documentation & handover</li></ul>',
    'offer2-details':
      '<ul class="space-y-2 text-sm text-gray-200"><li>⏱️ Timeline: <span class="font-semibold text-white">depends on scope</span></li><li>💰 Budget: <span class="font-semibold text-white">quoted</span> (SME-friendly budgets)</li><li>📍 Workshops possible in <span class="font-semibold text-white">Perpignan</span> and surrounding areas</li></ul>',

    'offer3-badge': 'Agencies & companies',
    'offer3-title': 'Team augmentation / Partnership',
    'offer3-subtitle':
      'Reliable senior support on Flutter / React / Next / Nest, direct or white-label.',
    'offer3-who':
      'Agencies, consultancies or companies needing short-term or recurring engineering support.',
    'offer3-what':
      '<ul class="space-y-2 text-sm text-gray-200"><li>✔️ Fast integration (tooling, standards, rituals)</li><li>✔️ Flutter / React / Next / Nest delivery</li><li>✔️ Quality mindset: clean PRs, tests when required, documentation</li><li>✔️ White-label possible</li><li>✔️ Part-time or progressive ramp-up</li></ul>',
    'offer3-formats':
      '<ul class="space-y-2 text-sm text-gray-200"><li>• Time & materials</li><li>• Fixed-price project</li><li>• Full subcontracting</li></ul>',
    'offer3-details':
      '<ul class="space-y-2 text-sm text-gray-200"><li>💰 Company daily rate: <span class="font-semibold text-white">from €500 (excl. VAT)</span> (depending on stack & involvement)</li><li>📍 Perpignan / Occitanie: <span class="font-semibold text-white">remote</span> or <span class="font-semibold text-white">on-site</span></li></ul>',

    'cred-title': 'Proof & credibility',
    'cred-list':
      '<ul class="mt-3 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2 text-sm text-gray-200"><li>✔️ 2 senior developers</li><li>✔️ 10 years of experience each</li><li>✔️ Web & mobile apps shipped to production</li><li>✔️ Maintainable, documented, quality-focused code</li><li>✔️ French company (SAS)</li><li>✔️ Pragmatic, results-driven approach</li></ul>',

    'offers-cta-primary': 'Talk about your project',
    'offers-cta-secondary': 'Request a quote',

    // Team section
    'team-title': 'Our Team',
    'laurent-role': 'President of DevMeUp',
    'laurent-desc1':
      'Backend-oriented Full-Stack Developer specialized in PHP, I have solid experience in creating complex and scalable web applications.',
    'laurent-desc2':
      'Specialized in RESTful APIs, I ensure the proper functioning of server architectures while working closely with front-end teams for optimal integrations',
    'jeremy-role': 'Co-founder of DevMeUp',
    'jeremy-desc1':
      'Front-End oriented Full-Stack Developer specialized in React and Flutter, I have solid experience in creating performant and responsive user interfaces.',
    'jeremy-desc2':
      'Passionate about innovation, I strive to integrate best practices to ensure clean and scalable code.',
    'kate-role': 'Digital Marketing Manager of DevMeUp',
    'kate-desc1':
      'Managing digital and client-facing communications, including content strategy, social media, PR, and AI-driven content creation.',
    'kate-desc2':
      'Supporting client acquisition and new projects through a strategic online presence.',

    // Contact section
    'contact-title': 'Contact Us',
    'contact-hook': 'Do you have a project or a technical need?',
    'contact-subhook': '📩 Reach out for a no-obligation discussion.',
    'name-label': 'Name',
    'name-placeholder': 'Name',
    'email-label': 'Email',
    'email-placeholder': 'Email',
    'company-label': 'Company',
    'company-placeholder': 'Company',
    'phone-label': 'Phone',
    'phone-placeholder': 'Phone',
    'message-label': 'Your message',
    'message-placeholder': 'Your message',
    'privacy-notice':
      'By submitting this form, you agree that the DevMeUp website stores and uses your personal data in order to contact you. They are kept for 30 days at most by our message reception service. The site editor undertakes not to transmit your contact details to any third party or use them for advertising. The form is protected against bots by Cloudflare Turnstile, without cookies.',
    'accept-label': ' I authorize DevMeUp to communicate with me ',
    'send-button': 'Request a quote / Book a call',
    'quote-text': 'A QUOTE,',
    'advice-text': 'ADVICE,',
    'project-text': 'A PROJECT',
    'contact-us-text': "Don't hesitate, contact us!",
    'success-message': 'Message sent successfully!',
    'error-message': 'Error sending message',

    // Additional contact form fields
    contact_subtitle: 'Ready to bring your project to life? Contact us to discuss your needs.',
    address: 'Address',
    name: 'Full name',
    name_placeholder: 'Your full name',
    email_placeholder: 'your@email.com',
    phone_placeholder: 'Your phone number',
    service_type: 'Service type',
    select_service: 'Select a service',
    web_development: 'Web Development',
    mobile_development: 'Mobile Development',
    ui_design: 'Interface Design',
    other: 'Other',
    message: 'Message',
    message_placeholder: 'Describe your project...',
    send_message: 'Send message',

    // Form validation and status
    'form.submit': 'Send',
    'form.sending': 'Sending...',
    'form.success.title': 'Message sent!',
    'form.success.message': 'We will get back to you as soon as possible.',
    'form.error.title': 'Error',
    'form.error.submit': 'An error occurred while sending. Email us directly at contact@devmeup.fr.',
    'form.error.network': 'Connection error. Please try again.',
    'form.error.captcha': 'The anti-bot check did not succeed. Wait a moment, then send the form again.',
    'form.error.fields': 'Please check your name, email address and message.',
    'form.error.rateLimited': 'You have sent several messages in a short time. Please try again in a few minutes.',
    'form.error.required': 'This field is required',
    'form.error.email': 'Please enter a valid email address',
    'form.error.phone': 'Please enter a valid phone number',
    'form.error.minLength': 'Message must contain at least 10 characters',

    // Footer
    'footer-text':
      '&copy; 2026 DevMeUp. All rights reserved. <a href="mailto:contact@devmeup.fr" class="hover:underline">contact@devmeup.fr</a><br/>📍 Perpignan (66) – web & mobile development<br/>⏱️ Reply within 48h<br/>🔒 GDPR compliant',
    'scroll-top-aria': 'Back to top',

    // Portfolio section
    'portfolio-title': 'Portfolio',
    'portfolio-subtitle': 'Discover our achievements and mobile apps.',
    'portfolio-dracailles-title': 'Fab Dracailles',
    'portfolio-dracailles-desc': 'Point counter app for card games',
    'portfolio-dracailles-link': 'View on Play Store',
    'portfolio-alarm-title': 'My Alarm',
    'portfolio-alarm-desc':
      '🚨 New app by DevMeUp!\nA smart alarm that adapts to your schedule\n🗓 Every other week or every week, you choose!\nNo need to reprogram everything, it does it for you 😎\nTry it and let us know what you think 🔥',
    'portfolio-alarm-link': 'View on Play Store',
    'portfolio-coming-title': 'Coming soon',
    'portfolio-coming-desc': 'New projects will be added to our portfolio soon.',
  },
};

export const getTranslation = (lang: Language, key: string): string => {
  return translations[lang]?.[key] ?? key;
};

export const isValidLanguage = (lang: string): lang is Language => {
  return lang === 'fr' || lang === 'en';
};
